'use client'

import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import io from 'socket.io-client'

export default function TerminalChatPage() {
  const [rooms, setRooms] = useState<any[]>([])
  const [currentRoom, setCurrentRoom] = useState<any>(null)
  const [messages, setMessages] = useState<any[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(true)
  const [hasAccess, setHasAccess] = useState(true)
  const [typingUsers, setTypingUsers] = useState<string[]>([])
  const [roomCounts, setRoomCounts] = useState<Record<string, number>>({})
  const [showUnreadBadge, setShowUnreadBadge] = useState(false)
  const [audioEnabled, setAudioEnabled] = useState(true)
  const [userStatuses, setUserStatuses] = useState<Record<string, string>>({})
  const [currentUser, setCurrentUser] = useState<any>(null)
  
const chatContainerRef = useRef<HTMLDivElement>(null)
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const afkTimerRef = useRef<NodeJS.Timeout | null>(null)
  const isAfkRef = useRef(false)
  const socketRef = useRef<ReturnType<typeof io> | null>(null)

  const playTerminalSound = (type: 'send' | 'receive') => {
    if (!audioEnabled) return
    try {
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext
      if (!AudioContext) return
      const ctx = new AudioContext()
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()

      osc.connect(gain)
      gain.connect(ctx.destination)

      if (type === 'send') {
        osc.type = 'square'
        osc.frequency.setValueAtTime(600, ctx.currentTime)
        osc.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.04)
        gain.gain.setValueAtTime(0.04, ctx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.04)
        osc.start()
        osc.stop(ctx.currentTime + 0.04)
      } else if (type === 'receive') {
        osc.type = 'sine'
        osc.frequency.setValueAtTime(440, ctx.currentTime)
        osc.frequency.setValueAtTime(880, ctx.currentTime + 0.06)
        gain.gain.setValueAtTime(0.03, ctx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12)
        osc.start()
        osc.stop(ctx.currentTime + 0.12)
      }
    } catch (e) {}
  }

  // Initialize WebSockets
  useEffect(() => {
    socketRef.current = io()

    socketRef.current.on('room_counts', (counts: any) => {
      setRoomCounts(counts)
    })

    socketRef.current.on('receive_message', (newMsg: any) => {
      setMessages(prev => {
        if (prev.some(m => m.id === newMsg.id)) return prev
        playTerminalSound('receive')

        const container = chatContainerRef.current
        if (container) {
          const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 120
          if (!isNearBottom) setShowUnreadBadge(true)
        }
        return [...prev, newMsg]
      })
    })

    socketRef.current.on('user_typing', (data: any) => {
      if (data.isTyping) {
        setTypingUsers(prev => Array.from(new Set([...prev, data.username])))
      } else {
        setTypingUsers(prev => prev.filter(u => u !== data.username))
      }
    })

    socketRef.current.on('presence_update', (data: any) => {
      setUserStatuses(prev => ({ ...prev, [data.userId]: data.status }))
    })

    return () => {
      socketRef.current?.disconnect()
    }
  }, [])

  // Idle / AFK Timer Setup
  useEffect(() => {
    if (!currentRoom || !socketRef.current) return

    const sendStatus = (status: 'ONLINE' | 'AFK') => {
      socketRef.current?.emit('status_change', { status, roomId: currentRoom.id })
    }

    const resetAfkTimer = () => {
      if (isAfkRef.current) {
        isAfkRef.current = false
        sendStatus('ONLINE')
      }
      if (afkTimerRef.current) clearTimeout(afkTimerRef.current)

      afkTimerRef.current = setTimeout(() => {
        isAfkRef.current = true
        sendStatus('AFK')
      }, 180000)
    }

    window.addEventListener('mousemove', resetAfkTimer)
    window.addEventListener('keydown', resetAfkTimer)
    window.addEventListener('click', resetAfkTimer)

    resetAfkTimer()

    return () => {
      window.removeEventListener('mousemove', resetAfkTimer)
      window.removeEventListener('keydown', resetAfkTimer)
      window.removeEventListener('click', resetAfkTimer)
      if (afkTimerRef.current) clearTimeout(afkTimerRef.current)
    }
  }, [currentRoom])

  useEffect(() => {
    async function loadRooms() {
      try {
        const res = await fetch('/api/chat/rooms')
        const data = await res.json()
        if (res.status === 403) {
          setHasAccess(false)
          setLoading(false)
          return
        }
        if (data.success && data.rooms.length > 0) {
          setRooms(data.rooms)
          setCurrentRoom(data.rooms[0])
          
          setCurrentUser({ id: data.userId || 'developer', name: data.username || 'User' })
          
          const initialCounts: Record<string, number> = {}
          data.rooms.forEach((r: any) => { initialCounts[r.id] = r.onlineCount || 0 })
          setRoomCounts(initialCounts)

          setHasAccess(true)
        } else {
          setHasAccess(false)
        }
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    }
    loadRooms()
  }, [])

  useEffect(() => {
    if (!currentRoom || !socketRef.current) return

    setMessages([])
    setTypingUsers([])
    setShowUnreadBadge(false)

    // Join the WebSocket room
    socketRef.current.emit('join_room', currentRoom.id, currentUser || { name: 'Developer' })

    // Load initial message history from DB
    async function loadHistory() {
      try {
        const res = await fetch(`/api/chat/messages?roomId=${currentRoom.id}`)
        const data = await res.json()
        if (data.success) {
          setMessages(data.messages)
          setTimeout(() => {
            if (chatContainerRef.current) {
              chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
            }
          }, 50)
        }
      } catch (e) {}
    }
    loadHistory()

  }, [currentRoom, currentUser])

  useEffect(() => {
    const container = chatContainerRef.current
    if (!container) return

    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 120
    if (isNearBottom) {
      container.scrollTop = container.scrollHeight
      setShowUnreadBadge(false)
    }
  }, [messages.length])

  const handleScroll = () => {
    const container = chatContainerRef.current
    if (!container) return
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 120
    if (isNearBottom) setShowUnreadBadge(false)
  }

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
      setShowUnreadBadge(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value)
    if (!currentRoom || !socketRef.current) return

    socketRef.current.emit('typing', {
      roomId: currentRoom.id,
      username: currentUser?.name || 'Developer',
      isTyping: true
    })

    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
    typingTimeoutRef.current = setTimeout(() => {
      socketRef.current?.emit('typing', {
        roomId: currentRoom.id,
        username: currentUser?.name || 'Developer',
        isTyping: false
      })
    }, 1500)
  }

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!input.trim() || !currentRoom) return

    const contentToSend = input
    setInput('')

    if (contentToSend.trim().toLowerCase() === '/clear') {
      setMessages([])
      return
    }

    playTerminalSound('send')

    socketRef.current?.emit('typing', {
      roomId: currentRoom.id,
      username: currentUser?.name || 'Developer',
      isTyping: false
    })

    try {
      const res = await fetch('/api/chat/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: contentToSend, roomId: currentRoom.id })
      })
      const data = await res.json()

      if (!data.success) {
        alert(data.error)
      } else {
        socketRef.current?.emit('send_message', { 
          roomId: currentRoom.id, 
          message: data.message || data.newMessage || data 
        })
        scrollToBottom()
      }
    } catch (err) {
      alert('Transmission failed')
    }
  }

  const renderFormattedContent = (content: string) => {
    if (content.includes('```')) {
      const parts = content.split('```')
      return parts.map((part, index) => {
        if (index % 2 === 1) {
          return (
            <pre key={index} className="bg-slate-900 border border-slate-800 p-2.5 rounded my-1.5 text-emerald-300 font-mono text-[11px] overflow-x-auto">
              <code>{part.trim()}</code>
            </pre>
          )
        }
        return <span key={index}>{part}</span>
      })
    }
    return <span>{content}</span>
  }

  if (loading) return <div className="min-h-screen bg-slate-950 text-indigo-400 font-mono p-8 text-xs">Initializing secure terminal stream...</div>

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 p-8 font-mono flex flex-col items-center justify-center">
        <div className="bg-slate-900 border border-slate-800 p-8 rounded-lg max-w-md text-center shadow-2xl">
          <div className="text-rose-400 font-bold text-sm mb-2">ACCESS RESTRICTED [403]</div>
          <p className="text-xs text-slate-400 mb-6 leading-relaxed">This secure terminal channel requires an active Chat Pass security clearance.</p>
          <Link href="/store" className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs py-2.5 px-6 rounded transition-colors shadow-lg">
            Acquire Chat Pass in Store →
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-mono flex flex-col p-4 md:p-8 max-w-[1600px] mx-auto">
      <div className="flex flex-col lg:flex-row gap-6 items-start">
        
        <div className="w-full lg:w-[260px] shrink-0 bg-slate-900 border border-slate-800 rounded-xl p-4 flex flex-col">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 px-2">
            Secure Channels
          </h3>
          <div className="space-y-1">
            {rooms.map(room => {
              const active = currentRoom?.id === room.id
              const onlineCount = roomCounts[room.id] || 0
              return (
                <button
                  key={room.id}
                  onClick={() => setCurrentRoom(room)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-xs font-semibold transition-colors flex items-center justify-between ${
                    active 
                      ? 'bg-indigo-600/20 text-indigo-300 border border-indigo-500/30' 
                      : 'text-slate-400 hover:bg-slate-800/80 hover:text-slate-200'
                  }`}
                >
                  <span className="truncate pr-2"># {room.name}</span>
                  <span className="text-[10px] text-slate-500 font-normal shrink-0">
                    ({onlineCount} online)
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        <div className="flex-1 w-full min-w-0 flex flex-col h-[calc(100vh-120px)] min-h-[650px] relative">
          <div className="bg-slate-900 border border-slate-800 rounded-t-lg px-4 py-3 flex items-center justify-between text-xs text-slate-400 shrink-0">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-rose-500/80 inline-block"></span>
              <span className="w-3 h-3 rounded-full bg-amber-500/80 inline-block"></span>
              <span className="w-3 h-3 rounded-full bg-emerald-500/80 inline-block"></span>
              <span className="ml-2 text-indigo-400 font-bold">~/devyield/secure-chat/{currentRoom?.slug || 'room'}</span>
            </div>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setAudioEnabled(!audioEnabled)}
                className={`text-[10px] px-2 py-0.5 rounded border transition-colors cursor-pointer ${
                  audioEnabled 
                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20' 
                    : 'bg-slate-800 text-slate-500 border-slate-700 hover:text-slate-400'
                }`}
                title={audioEnabled ? 'Mute terminal audio' : 'Unmute terminal audio'}
              >
                {audioEnabled ? '🔊 AUDIO: ON' : '🔇 AUDIO: OFF'}
              </button>

              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                <span className="text-[10px] text-emerald-400 font-bold">WSS_LINK_ESTABLISHED</span>
              </div>
            </div>
          </div>

          <div className="relative flex-1 flex flex-col min-h-0">
            <div 
              ref={chatContainerRef} 
              onScroll={handleScroll}
              className="flex-1 bg-slate-950 border-x border-slate-800 p-4 overflow-y-auto space-y-3 text-xs"
            >
              <div className="text-slate-600 border-b border-slate-900 pb-2 mb-4 font-mono">
                Connected to channel <span className="text-indigo-400 font-bold">#{currentRoom?.name}</span>. {currentRoom?.description}
              </div>
              
              {messages.map((msg) => {
                const user = msg.user
                const username = user?.username || user?.name || 'unknown'
                const badges = user?.badges || []
                const cosmetics = user?.cosmetics || []
                const status = userStatuses[user?.id] || 'ONLINE'

                return (
                  <div key={msg.id} className="flex flex-wrap items-center gap-2 leading-relaxed">
                    <span className="text-slate-600 select-none shrink-0">
                      [{new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}]
                    </span>

                    <span title={status} className="text-[10px]">
                      {status === 'AFK' ? '🌙' : '🟢'}
                    </span>

                    <div className="flex items-center gap-1.5 flex-wrap">
                      {badges.map((ub: any) => (
                        <span key={ub.badge.id} title={ub.badge.description} className="inline-flex items-center gap-1 text-[10px] bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 px-1.5 py-0.5 rounded font-sans">
                          <span>{ub.badge.icon}</span>
                        </span>
                      ))}
                      {cosmetics.map((uc: any) => (
                        <span key={uc.id} title={uc.item.description} className="inline-flex items-center gap-1 text-[10px] bg-amber-500/10 border border-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded font-sans">
                          <span>{uc.item.icon || '🛡️'}</span>
                        </span>
                      ))}
                    </div>

                    <Link href={`/profile/${user?.id}`} className="text-indigo-400 font-bold shrink-0 hover:underline">
                      @{username}:
                    </Link>

                    <div className="text-slate-300 break-all w-full pl-14 sm:pl-0 sm:w-auto">
                      {renderFormattedContent(msg.content)}
                    </div>
                  </div>
                )
              })}
            </div>

            {showUnreadBadge && (
              <button 
                onClick={scrollToBottom}
                className="absolute bottom-4 right-6 bg-indigo-600 hover:bg-indigo-500 text-white shadow-2xl px-3 py-1.5 rounded-full text-[11px] font-bold flex items-center gap-1.5 transition-all animate-bounce cursor-pointer z-10 border border-indigo-400/40"
              >
                <span>↓ New messages received</span>
              </button>
            )}
          </div>

          <div className="px-4 py-0.5 bg-slate-950 border-x border-slate-800 text-[10px] text-slate-500 italic h-6 flex items-center shrink-0">
            {typingUsers.length > 0 ? `${typingUsers.join(', ')} ${typingUsers.length === 1 ? 'is' : 'are'} transmitting...` : ''}
          </div>

          <form onSubmit={sendMessage} className="bg-slate-900 border border-slate-800 rounded-b-lg p-3 flex items-center gap-2 shrink-0">
            <span className="text-emerald-400 font-bold pl-2">$</span>
            <input 
              type="text"
              value={input}
              onChange={handleInputChange}
              placeholder={`Transmit message to #${currentRoom?.name || 'room'} (try /roll <pts>)...`}
              className="bg-transparent flex-1 text-xs text-white focus:outline-none font-mono placeholder:text-slate-600"
              autoFocus
            />
            <button 
              type="submit"
              className="bg-indigo-600/20 hover:bg-indigo-600/40 text-indigo-300 border border-indigo-500/30 px-4 py-1.5 rounded text-[11px] transition-colors cursor-pointer"
            >
              Send
            </button>
          </form>
        </div>

      </div>
    </div>
  )
}